import { useEffect } from "react";
import Close from "@mui/icons-material/Close";
import HorizontalRule from "@mui/icons-material/HorizontalRule";
import {
  Box,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import { SubmissionButton } from "@thalamos/frontend";
import type {
  ExtendedVisionUser,
  IncidentWorkItemId,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  calculateActions,
  getPatientName,
  ParticipantStatusSchema,
  tk,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { HeadingAndSubheading } from "../../components/IncidentWorkItems/IncidentWorkItemCard/IncidentWorkItemCard.js";
import { renderUkDateTime } from "../../components/util.js";
import type { UserContextType } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";

interface TransferRequestedModalInnerProps {
  user: ExtendedVisionUser;
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  open: boolean;
  close: () => void;
  refetchUser: UserContextType["refetchUser"];
}
interface TransferRequestedModalProps {
  user: ExtendedVisionUser;
  incidentWorkItemId: IncidentWorkItemId;
  notificationId: string;
  open: boolean;
  close: () => void;
  refetchUser: UserContextType["refetchUser"];
}

export function TransferRequestedModalInner({
  incidentWorkItem,
  open,
  close,
  refetchUser,
}: TransferRequestedModalInnerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const notifications = useToastNotifications();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));

  const render = (
    incidentWorkItem: IncidentWorkItemWithContext | undefined,
  ) => {
    const containerStyles = {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
      minHeight: "12rem",
      justifyContent: "space-between",
      ...(isMobileView && {
        border: "1px solid",
        borderRadius: "0.5rem",
        borderColor: theme.palette.grey[400],
        padding: "1rem",
      }),
    };

    const handleAcceptTransfer = async () => {
      // resolveTransfer is a single atomic call: the server clears the
      // transfer notification as part of accepting, so there is no separate
      // markAsRead request that could fail on a flaky connection.
      const response = await api.incidentWorkItem.resolveTransfer({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        incidentWorkItemId: incidentWorkItem!.incidentWorkItem.id,
        isAccepted: true,
      });

      if (response.status === 204) {
        notifications.show(t("incidentWorkItem.notificationAcceptedShow"), {
          severity: "success",
        });
        close();
        navigate(
          createRoute.incidentProgressPage(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            incidentWorkItem!.incidentWorkItem.id,
          ),
        );
      } else {
        notifications.show(t("incidentWorkItem.notificationAcceptedError"), {
          severity: "error",
        });
        close();
      }
    };

    const handleRejectTransfer = async () => {
      // Single atomic call: the server clears the transfer notification as
      // part of rejecting, so there is no separate markAsRead request.
      const response = await api.incidentWorkItem.resolveTransfer({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        incidentWorkItemId: incidentWorkItem!.incidentWorkItem.id,
        isAccepted: false,
      });

      if (response.status === 204) {
        notifications.show(t("incidentWorkItem.notificationRejectedShow"), {
          severity: "success",
        });
        await refetchUser();
      } else {
        notifications.show(t("incidentWorkItem.notificationRejectedError"), {
          severity: "error",
        });
      }

      close();
    };

    const getRequestingOfficerName = (
      incidentWorkItem: IncidentWorkItemWithContext,
    ) => {
      const requestingOfficerName = incidentWorkItem.participants.find(
        (participant) => {
          return (
            participant.status ===
            ParticipantStatusSchema.enum.requestedTransfer
          );
        },
      );

      return requestingOfficerName?.name ?? "";
    };

    const getIncidentCreatedDateTime = (
      incidentWorkItem: IncidentWorkItemWithContext,
    ) =>
      renderUkDateTime(
        dayjs(incidentWorkItem.incidentWorkItem.createdAt).toDate(),
        true,
      );
    const getFormattedPatientName = (
      incidentWorkItem: IncidentWorkItemWithContext,
    ) => {
      const { surname, forename } = getPatientName({ incidentWorkItem });
      if (!forename && !surname) {
        return t("common.unknown");
      }
      return [surname, forename].filter(Boolean).join(", ");
    };

    return (
      <PopupDialog
        open={open}
        onClose={close}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        disableDialogRootPadding={false}
        hasPadding={false}
      >
        {incidentWorkItem === undefined ? (
          <CircularProgress />
        ) : (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "1.250rem",
                fontWeight: "bold",
              }}
            >
              {t("transfer.requestedTransfer.title")}
              <IconButton aria-label="close" onClick={close}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
              <Typography
                sx={{
                  color: "primary",
                  fontSize: "0.875rem",
                  paddingBottom: "1rem",
                }}
              >
                {t("transfer.requestedTransfer.officerRequest", {
                  requestingOfficerName:
                    getRequestingOfficerName(incidentWorkItem),
                })}
              </Typography>

              <Box sx={containerStyles}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: "repeat(2, 1fr)",
                  }}
                >
                  <HeadingAndSubheading
                    heading={tk("transfer.requestedTransfer.createdAt")}
                    subheading={getIncidentCreatedDateTime(incidentWorkItem)}
                  />
                  <HeadingAndSubheading
                    heading={tk("transfer.requestedTransfer.nameOfPerson")}
                    subheading={getFormattedPatientName(incidentWorkItem)}
                  />
                  <HorizontalRule />
                  <HorizontalRule />
                  <HeadingAndSubheading
                    heading={tk("transfer.requestedTransfer.incidentId")}
                    subheading={incidentWorkItem.incidentWorkItem.id}
                  />
                  <HeadingAndSubheading
                    heading={tk("transfer.requestedTransfer.officerAssigned")}
                    subheading={getRequestingOfficerName(incidentWorkItem)}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "1rem",
                    padding: "0.5rem 0",
                  }}
                >
                  <SubmissionButton
                    variant="outlined"
                    color="error"
                    onClick={handleRejectTransfer}
                    sx={{ minWidth: "5rem" }}
                  >
                    {t("transfer.requestedTransfer.reject")}
                  </SubmissionButton>

                  <SubmissionButton
                    variant="contained"
                    onClick={handleAcceptTransfer}
                    sx={{ minWidth: "5rem" }}
                  >
                    {t("transfer.requestedTransfer.accept")}
                  </SubmissionButton>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </PopupDialog>
    );
  };

  return render(incidentWorkItem);
}

export function TransferRequestedModal({
  user,
  incidentWorkItemId,
  notificationId,
  open,
  close,
  refetchUser,
}: TransferRequestedModalProps) {
  const { t } = useTranslation();
  const toastNotifications = useToastNotifications();
  const [{ data: incidentWorkItem, error, response }] =
    apiHooks.incidentWorkItem.get(incidentWorkItemId);

  const actions = incidentWorkItem
    ? calculateActions({
        incidentWorkItem,
        user,
        asOfDateTime: dayjs(),
      })
    : null;

  // Clear the notification if user cannot accept/reject (e.g. incident ended)
  useEffect(() => {
    if (
      response?.status === 200 &&
      incidentWorkItem &&
      !actions?.transfers.canAcceptReject
    ) {
      void api.notifications
        .markAsRead(notificationId)
        .catch(() => {
          toastNotifications.show(t("notifications.failedMarkAsRead"), {
            severity: "error",
          });
        })
        .then(close);
    }
  }, [actions, incidentWorkItem]);

  if (error) return <ErrorComponent />;

  return (
    <TransferRequestedModalInner
      user={user}
      incidentWorkItem={incidentWorkItem ?? undefined}
      open={open}
      close={close}
      refetchUser={refetchUser}
    />
  );
}
